import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../../hooks/useFetch";

const HAFHContactList = ({ t, role, hostelCode }) => {
  const authState = useSelector((state) => state.authReducer);
  const [fetchData] = useFetch();

  const [contacts, setContacts] = useState([]);
  const [selectedType, setSelectedType] = useState("ALL");

  const contactTypes = [
    "EMERGENCY",
    "WARDEN",
    "SECURITY",
    "TRANSPORT",
    "MESS",
    "MEDICAL",
    "PARENT",
    "POLICE",
    "FIRE",
    "AMBULANCE",
    "ADMIN",
    "MAINTENANCE",
    "OTHER",
  ];

  const fetchContacts = async () => {
    try {
      const res = await fetchData({
        url: "/hafh/communication/contacts/all",
        method: "post",
        data: { hostelCode },
        headers: {
          Authorization: authState.token,
        },
      });

      if (res?.status) {
        setContacts(res.records || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (hostelCode) fetchContacts();
  }, [hostelCode]);

  const filtered =
    selectedType === "ALL"
      ? contacts
      : contacts.filter((c) => c.contactType === selectedType);

  const handleCall = (number) => {
    if (!number) return alert("Number not available");
    window.location.href = `tel:${number}`;
  };

  const handleEmail = (email) => {
    if (!email) return alert("Email not available");
    window.location.href = `mailto:${email}`;
  };

  const handleWhatsApp = (number, name = "Hello") => {
    if (!number) return alert("WhatsApp number not available");

    const msg = encodeURIComponent(
      `Hi ${name}, I am contacting you from Think5Whys Hostel.`
    );

    const cleanNumber = number.replace(/[^0-9]/g, ""); // removes +, -, spaces
    window.open(`https://wa.me/${cleanNumber}?text=${msg}`, "_blank");
  };

  const handleShare = async (c) => {
    const text = `
        📞 Hostel Contact
        Name: ${c.contactName}
        Person: ${c.contactPerson || "-"}
        Phone: ${c.contactNumber}
        Email: ${c.emailId || "-"}
    `;

    if (navigator.share) {
      await navigator.share({
        title: "Hostel Contact",
        text,
      });
    } else {
      await navigator.clipboard.writeText(text);
      alert("Copied to clipboard");
    }
  };

  return (
    <div className="bg-gray-900 p-5 rounded-xl text-white space-y-4">

      <h2 className="text-yellow-300 text-lg font-bold">
        📞 Hostel Contact Directory
      </h2>

      {/* FILTERS */}
      <div className="grid grid-cols-3 lg:grid-cols-7 gap-1">
        <button
          onClick={() => setSelectedType("ALL")}
          className={`px-1 py-2 rounded-md text-xs lg:text-sm ${
            selectedType === "ALL" ? "bg-teal-600" : "bg-gray-700"
          }`}
        >
          ALL
        </button>

        {contactTypes.map((type) => (
          <button
            key={type}
            onClick={() => setSelectedType(type)}
            className={`px-1 py-2 rounded-md text-xs lg:text-sm ${
              selectedType === type ? "bg-teal-600" : "bg-gray-700"
            }`}
          >
            {type}
          </button>
        ))}
      </div>

      {/* LIST */}
      <div className="space-y-3 mt-4">
        {filtered.length === 0 ? (
          <div className="text-gray-400 text-sm">
            No contacts found for this category
          </div>
        ) : (
          filtered.map((c) => {
            const phone = c.contactNumber;
            const email = c.emailId;

            const whatsappMessage = encodeURIComponent(
              `Hello ${c.contactPerson || c.contactName}, I am contacting you regarding hostel support (${hostelCode}).`
            );

            return (
              <div
                key={c._id}
                className="bg-gray-800 p-3 rounded-xl border border-gray-700 space-y-2"
              >
                {/* HEADER */}
                <div className="flex justify-between">
                  <div className="text-sm font-semibold text-cyan-300">
                    {c.contactName}
                  </div>
                  <div className="text-xs text-yellow-300">
                    {c.contactType}
                  </div>
                </div>

                <div className="text-sm text-gray-200">
                  👤 {c.contactPerson || "N/A"} ({c.contactRole || "N/A"})
                </div>

                <div className="text-sm text-gray-300">
                  📞 {phone}
                </div>

                {email && (
                  <div className="text-xs text-gray-400">
                    ✉️ {email}
                  </div>
                )}

                {c.isEmergencyContact && (
                  <div className="text-red-400 text-xs">
                    🚨 Emergency Contact
                  </div>
                )}

                {/* ACTION BUTTONS */}
                <div className="flex flex-wrap gap-2 mt-2">

                  {/* CALL */}
                  <a
                    href={`tel:${phone}`}
                    className="px-3 py-1 bg-green-600 hover:bg-green-500 text-xs rounded-md"
                  >
                    📞 Call
                  </a>

                  {/* EMAIL */}
                  {email && (
                    <a
                      href={`mailto:${c.emailId?.trim()}`}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-xs rounded-md"
                    >
                      ✉️ Email
                    </a>
                  )}
                  {/* WHATSAPP */}
                  <a
                    href={`https://wa.me/${phone.replace(/[^0-9]/g, "")}?text=${whatsappMessage}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1 bg-emerald-600 hover:bg-emerald-500 text-xs rounded-md"
                  >
                    🟢 WhatsApp
                  </a>

                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default HAFHContactList;